import { MouseEvent, useMemo, useState } from "react";
import { Box, Button, Link } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CustomModal from "../modal/modal.component";
import {
  buildEntityDetailRoute,
  EntityReferenceType,
} from "../../../utils/entityRoutes";
import { EntityPreviewPage } from "./entityPreviewPage.component";

const ENTITY_ROUTE_MATCHERS: Record<EntityReferenceType, RegExp> = {
  client: /^\/clients\/([^/]+)(?:\/|$)/,
  contract: /^\/contracts\/([^/]+)(?:\/|$)/,
  equipment: /^\/equipment-section\/equipments\/([^/]+)(?:\/|$)/,
  job: /^\/service\/jobs\/([^/]+)(?:\/|$)/,
};

type EntityReferenceLinkProps = {
  entityType: EntityReferenceType;
  entityId?: string | number | null;
  label?: string | null;
  canPreview?: boolean;
  stopPropagation?: boolean;
  modalTitle?: string;
};

const handleCellClickEvent = (
  event: MouseEvent<HTMLElement>,
  shouldStopPropagation: boolean
) => {
  event.preventDefault();
  if (shouldStopPropagation) {
    event.stopPropagation();
  }
};

export default function EntityReferenceLink({
  entityType,
  entityId,
  label,
  canPreview = true,
  stopPropagation = true,
  modalTitle,
}: EntityReferenceLinkProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [opened, setOpened] = useState(false);

  const displayValue = label?.trim() ? label : "-";
  const route = useMemo(() => {
    if (!entityId) {
      return "";
    }
    return buildEntityDetailRoute(entityType, entityId);
  }, [entityId, entityType]);

  const isCurrentEntityRoute = useMemo(() => {
    if (!entityId) {
      return false;
    }
    const matcher = ENTITY_ROUTE_MATCHERS[entityType];
    const match = location.pathname.match(matcher);

    if (!match?.[1]) {
      return false;
    }

    return String(match[1]) === String(entityId);
  }, [entityId, entityType, location.pathname]);

  if (!canPreview || !entityId || displayValue === "-" || isCurrentEntityRoute) {
    return (
      <Box component="span" sx={{ fontSize: "0.875rem", lineHeight: 1.4 }}>
        {displayValue}
      </Box>
    );
  }

  return (
    <>
      <Link
        component="button"
        type="button"
        underline="hover"
        onClick={(event) => {
          handleCellClickEvent(event, stopPropagation);
          setOpened(true);
        }}
        sx={{
          textAlign: "left",
          fontSize: "0.875rem",
          lineHeight: 1.4,
        }}
      >
        {displayValue}
      </Link>
      <CustomModal
        opened={opened}
        setOpened={setOpened}
        width="min(1200px, 96vw)"
        title={modalTitle || t("Preview")}
        headerActions={
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setOpened(false);
              navigate(route);
            }}
          >
            {t("Open full view")}
          </Button>
        }
        content={
          <Box sx={{ width: "100%" }}>
            <EntityPreviewPage entityType={entityType} entityId={entityId} />
          </Box>
        }
      />
    </>
  );
}
